/**
 * What to do when someone identifies (PIX-605).
 *
 * The vendor holds exactly one distinct id per browser, but this app can have
 * two people authenticated at once: the student token set and the organisation
 * token set are independent browser-storage keys, and both stores identify on
 * every user fetch - including on boot. On a shared school machine that means a
 * teacher and a student can each try to own the same browser's identity.
 *
 * The harm to avoid is not "the wrong person wins" - one distinct id per
 * browser makes that unavoidable, and boot order decides it. The harm is the
 * two of them being *merged* into one person, which corrupts both their
 * histories permanently and cannot be undone. Resetting first severs the link.
 *
 * Reset is not free, though: it drops every super property and mints a new
 * anonymous id, which starts a new session. So it must happen only when the
 * person genuinely changes - resetting on every re-identify would inflate
 * session counts and wreck the sessions-per-user and return-rate metrics.
 *
 * Pure and framework-free so the rule is testable without a browser.
 */

export const IDENTITY_ACTIONS = Object.freeze({
  /** Nobody is identified yet - claim the current (anonymous) id. */
  IDENTIFY: 'identify',
  /** A different person is already identified - sever, then claim. */
  RESET_THEN_IDENTIFY: 'reset_then_identify',
  /** Already identified as this person, or nobody to identify. */
  NOOP: 'noop',
});

/**
 * Decide how to identify `nextId` given who is identified now.
 *
 * `currentIdentifiedId` is null whenever the facade has not identified anyone -
 * which includes the case where the vendor is carrying a purely anonymous id.
 * That is deliberately treated as "nobody", because merging an anonymous
 * browsing history into the account it becomes is exactly what we want; it is
 * only merging two *known* people that must never happen.
 *
 * @param {object}  input
 * @param {?string} input.currentIdentifiedId  who the facade last identified
 * @param {?string} input.nextId               who is identifying now
 * @returns {string} one of IDENTITY_ACTIONS
 */
export function decideIdentityAction({
  currentIdentifiedId = null,
  nextId = null,
} = {}) {
  if (!nextId) {
    return IDENTITY_ACTIONS.NOOP;
  }

  if (!currentIdentifiedId) {
    return IDENTITY_ACTIONS.IDENTIFY;
  }

  return String(currentIdentifiedId) === String(nextId)
    ? IDENTITY_ACTIONS.NOOP
    : IDENTITY_ACTIONS.RESET_THEN_IDENTIFY;
}
